import { computed, ref, onMounted, onUnmounted } from 'vue'
import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDateTime } from '@ai/ui-components'

export interface DateRange {
  datetimeFrom: number
  datetimeTo: number
  formattedString: string
}

export const useHomeDateRange = (): Ref<DateRange> => {
  const { t } = useI18n({ useScope: 'global' })
  const { formatDateTime } = useDateTime()
  const currentTime = ref(new Date())
  let intervalId: ReturnType<typeof setInterval> | null = null

  const dateRange = computed<DateRange>(() => {
    const now = currentTime.value

    // Calculate yesterday, rounded to the hour
    const yesterdayDate = new Date(now)
    yesterdayDate.setDate(yesterdayDate.getDate() - 1)
    yesterdayDate.setMinutes(0, 0, 0)

    // Round current time to end of current hour (XX:59:00)
    const nowRounded = new Date(now)
    nowRounded.setMinutes(59, 59, 0)

    const datetimeFrom = yesterdayDate.getTime()
    const datetimeTo = nowRounded.getTime()

    const formattedString = `${t('home.data_since')} ${formatDateTime({
      format: 'longDate',
      value: yesterdayDate,
    })} ${formatDateTime({
      format: 'time',
      value: yesterdayDate,
    })}`

    return {
      datetimeFrom,
      datetimeTo,
      formattedString,
    }
  })

  onMounted(() => {
    // Update time every minute
    intervalId = globalThis.setInterval(() => {
      currentTime.value = new Date()
    }, 60000)
  })

  onUnmounted(() => {
    if (intervalId) {
      clearInterval(intervalId)
    }
  })

  return dateRange
}
